import { Component, OnInit, HostListener, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Candidate, CANDIDATE_STATUS_LABELS } from '../../services/recruiter-data.service';
import { CandidateApiService } from '../../services/candidate-api.service';
import { ToastrService } from 'ngx-toastr';
import { catchError, debounceTime, distinctUntilChanged, finalize, of, Subject, tap } from 'rxjs';
import { CANDIDATE_STATUSES } from '@edtech-ui/core/constants/recuritment-constants/all-candidate-status';
import { CANDIDATE_LOCATIONS, CANDIDATE_ROLES } from '@edtech-ui/core/constants/recuritment-constants/candidate-roles';
import { sanitizeCity, validateCity, validateRange } from '@edtech-ui/core/utils/recruitment-utils/candidate-popup-filter';
import { EducatorValidators } from '@edtech-ui/core/utils/recruitment-utils/educator-validators';
import { INDIAN_LOCATIONS } from '@edtech-ui/core/constants/recuritment-constants/indian-locations.constants';
import { StudentJobEmailValidator } from '@edtech-ui/core/utils/student-apply-job/student-job-email';
import { StudentCityPreferenceValidator } from '@edtech-ui/core/utils/student-apply-job/student-job-locations';
import { StudentJobApplicantNameValidator } from '@edtech-ui/core/utils/student-apply-job/student-job-name';
import { CustomValidators } from '@edtech-ui/core/utils/student-apply-job/student-role';
import { StudentCommonValidation } from '@edtech-ui/core/utils/student-apply-job/student-education';
import { CtcValidator } from '@edtech-ui/core/utils/student-apply-job/ctc-ectc-valication';
import { RecruitmentJobService } from '@edtech-ui/core/services/recruitment-management/recruitment-job.service';
import { CompanyJob } from '@edtech-ui/core/models/recruitment/get-all-job-listing';
import { HasPermissionDirective } from '@edtech-ui/access-role-permissions/users-permissions-directives/has-permissions.directives';
import { PermissionForStudents } from '@edtech-ui/access-role-permissions/users-permissions-constants/student-roles-permissions.enum';
import { AuthServiceService } from '@edtech-ui/core/services/authService/auth-service.service';
import { MatTooltipModule } from '@angular/material/tooltip';


function mapApiToCandidate(api: any): Candidate {
	const statusMap: Record<string, Candidate['status']> = {
		new: 'new', applied: 'applied',
		assessment_pending: 'assessment_pending', assessment_completed: 'assessment_completed',
		ai_interview_pending: 'ai_interview_pending',
		interview_scheduled: 'interview_scheduled',
		shortlisted: 'shortlisted', selected: 'selected',
		hired: 'selected', rejected: 'rejected'
	};
	const typeMap: Record<string, Candidate['experienceType']> = {
		fresher: 'fresher', experienced: 'experienced', educator: 'educator',
	};
	return {
		id: api.candidate_id,
		name: api.name,
		email: api.email,
		mobile: api.mobile,
		role: api.role || (api.candidate_type === 'educator' ? 'educator' : ''),
		location: api.current_location || api.city_pref_1 || '—',
		assessmentScore: api.assessment_score ?? 0,
		interviewScore: api.ai_interview_score ?? 0,
		status: statusMap[api.status] ?? 'applied',
		final_status: api.final_status,
		appliedDate: api.created_at
			? new Date(api.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
			: '—',
		experienceType: typeMap[api.candidate_type] ?? 'fresher',
		currentTitle: api.current_title,
		currentCompany: api.current_company,
		experience: api.experience_years ? `${api.experience_years} yrs` : undefined,
		currentCtc: api.current_ctc?.toString(),
		expectedCtc: api.expected_ctc?.toString(),
		prefCity1: api.city_pref_1,
		prefCity2: api.city_pref_2,
		prefCity3: api.city_pref_3,
		subject: api.subject,
		specialization: api.subject_specialization,
		previousInstitute: api.college_name || api.previous_institute,
		resume_url: api.resume_url,
		job_title: api.job_title || '—',
		education: api.education,
		yearOfPassing: api.year_of_passing_out || api.year_of_passing,
		cgpa: api.cgpa,
		skills: api.skills,
		core_expertise: api.core_expertise,
	};
}

@Component({
	selector: 'app-tab-candidates',
	standalone: true,
	imports: [CommonModule, FormsModule, ReactiveFormsModule, HasPermissionDirective,
		MatTooltipModule
	],
	templateUrl: './tab-candidates.component.html',
	styleUrls: ['./tab-candidates.component.scss']
})
export class TabCandidatesComponent implements OnInit {
	@ViewChild('csvInput') csvInputRef!: ElementRef<HTMLInputElement>;

	allCandidates: Candidate[] = [];
	filteredCandidates: any[] = [];
	isLoading = false;
	isSubmitting = false;
	stats = { total: 0, onHold: 0, selected: 0, rejected: 0 };
	importResultMessage = '';

	currentPage = 1;
	totalPages = 1;
	totalCandidates = 0;
	hasNextPage = false;
	hasPreviousPage = false;
	limit = 15;
	private lastSearchQuery = '';

	candidateTab = '';
	candidateSearchQuery = '';
	selectedCandidateRole = '';
	selectedCandidateStatus = '';
	selectedCandidateExperience = '';
	selectedCandidateLocation = '';
	isFinalStatusDropdownOpen = false;
	isExperienceDropdownOpen = false;
	isLocationDropdownOpen = false;
	activeActionMenuId: string | null = null;

	isAddPopupOpen = false;
	isEditMode = false;
	editingCandidateId = '';
	popupExperienceType: 'fresher' | 'experienced' | 'educator' = 'educator';
	popupTab: 'Manual' | 'CSV' = 'Manual';

	// Fresher/common form fields
	fullName = '';
	email = '';
	mobile = '';
	role = '';
	currentLocation = '';
	education = '';
	cgpa = '';
	skills = '';

	// Experienced form fields
	currentTitle = '';
	currentCompany = '';
	coreExpertise = '';
	totalExperience = '';
	currentCtc = '';
	expectedCtc = '';
	prefCity1 = '';
	prefCity2 = '';
	prefCity3 = '';

	// Educator form fields
	eduSubject = '';
	eduSpecialization = '';
	eduExperience = '';
	eduCurrentCtc = '';
	eduExpectedCtc = '';
	eduPreviousInstitute = '';
	eduPrefCity1 = '';
	eduPrefCity2 = '';
	eduPrefCity3 = '';
	FresherCandidateForm: FormGroup;
	ExperiencedCandidateForm: FormGroup;
	EducatorCandidateForm: FormGroup;
	companyId = '';
	selectedResumeFile: File | null = null;
	isCandidateDelete: string | null = null;
	candidatesLocations = CANDIDATE_LOCATIONS;
	showAdvancedFilters = false;
	private searchSubject = new Subject<string>();
	validateRange = validateRange;
	validateCity = validateCity;
	public statusLabels = CANDIDATE_STATUS_LABELS;
	indianLocations = INDIAN_LOCATIONS;
	filteredCities1: string[] = [];
	filteredCities2: string[] = [];
	filteredCities3: string[] = [];
	showCityDropdown1 = false;
	showCityDropdown2 = false;
	showCityDropdown3 = false;

	// Educator city dropdown
	filteredEducatorCities1: string[] = [];
	filteredEducatorCities2: string[] = [];
	filteredEducatorCities3: string[] = [];

	showEducatorCityDropdown1 = false;
	showEducatorCityDropdown2 = false;
	showEducatorCityDropdown3 = false;


	// Experienced city dropdown
	filteredExperiencedCities1: string[] = [];
	filteredExperiencedCities2: string[] = [];
	filteredExperiencedCities3: string[] = [];

	showExperiencedCityDropdown1 = false;
	showExperiencedCityDropdown2 = false;
	showExperiencedCityDropdown3 = false;

	jobSearchQuery = '';
	selectedJobStatus = '';
	selectedJobType = '';
	selectedJobLocation = '';
	selectedVisibility: boolean | null = null;
	totalPage = 0;
	page = 1;
	total = 8;
	allPostJobs: CompanyJob[] = [];
	showJobDropdown = false;
	filteredJobs: any[] = [];
	selectedJobName = '';
	showCurrentLocationDropdown = false;
	filteredCurrentLocations: string[] = [];
	PermissionForStudents = PermissionForStudents;
	canViewFresher = false;
	canViewExperienced = false;
	canViewEducator = false;
	showDeleteConfirmPopup = false;
	selectedCandidateId: string | null = null;
	isJobLoading = false;
	showCandidateJobDropdown = false;
	jobLimit = 10;


	constructor(
		private candidateApi: CandidateApiService,
		private router: Router,
		private toastr: ToastrService,
		private jobService: RecruitmentJobService,
		private _authService: AuthServiceService,
	) {

		this.FresherCandidateForm = new FormGroup({
			name: new FormControl('', [Validators.required, StudentJobApplicantNameValidator()]),
			email: new FormControl('', [Validators.required, Validators.email, StudentJobEmailValidator()]),
			mobile: new FormControl('', [Validators.required, Validators.pattern('^[6-9][0-9]{9}$'), Validators.minLength(10), Validators.maxLength(10)]),
			role: new FormControl('', [Validators.required, CustomValidators.roleValidator()]),
			current_location: new FormControl('', [Validators.required, StudentCityPreferenceValidator()]),
			education: new FormControl('', [Validators.required, StudentCommonValidation.AdminEducationValidator()]),
			previous_institute: new FormControl('', [Validators.maxLength(150)]),
			year_of_passing: new FormControl('', [StudentCommonValidation.AdminYearOfPassingValidator()]),
			cgpa: new FormControl('', [StudentCommonValidation.AdminCandidateCgpaValidator()]),
			skills: new FormControl('', [StudentCommonValidation.AdminCandidateSkillsValidator()]),
			resume: new FormControl('')
		});


		this.ExperiencedCandidateForm = new FormGroup({
			name: new FormControl('', [Validators.required, StudentJobApplicantNameValidator()]),
			email: new FormControl('', [Validators.required, Validators.email, StudentJobEmailValidator()]),
			mobile: new FormControl('', [Validators.required, Validators.pattern('^[6-9][0-9]{9}$'), Validators.minLength(10), Validators.maxLength(10)]),
			role: new FormControl('', [Validators.required, CustomValidators.roleValidator()]),
			current_location: new FormControl('', [Validators.required, StudentCityPreferenceValidator()]),
			education: new FormControl('', [Validators.required, StudentCommonValidation.AdminEducationValidator()]),
			previous_institute: new FormControl('', [Validators.maxLength(150)]),
			year_of_passing: new FormControl('', [StudentCommonValidation.AdminYearOfPassingValidator()]),
			current_title: new FormControl('', [Validators.required, StudentCommonValidation.JobCandidateTitleValidator()]),
			current_company: new FormControl('', [Validators.required, StudentCommonValidation.CompanyCandidateNameValidator()]),
			core_expertise: new FormControl(''),
			experience_years: new FormControl(null, [Validators.required, Validators.pattern(/^(50(\.0+)?|[0-4]?\d(\.\d+)?)$/), Validators.min(0), Validators.max(50)]),
			current_ctc: new FormControl('', [Validators.required, CtcValidator()]),
			expected_ctc: new FormControl('', [Validators.required, CtcValidator()]),
			city_pref_1: new FormControl('', StudentCityPreferenceValidator()),
			city_pref_2: new FormControl('', StudentCityPreferenceValidator()),
			city_pref_3: new FormControl('', StudentCityPreferenceValidator()),
			resume: new FormControl('')
		});

		this.EducatorCandidateForm = new FormGroup({
			name: new FormControl('', [Validators.required, EducatorValidators.educatorName]),
			email: new FormControl('', [Validators.required, EducatorValidators.email]),
			mobile: new FormControl('', [Validators.required, EducatorValidators.mobile]),
			subject: new FormControl('', [Validators.required, EducatorValidators.subject]),
			subject_specialization: new FormControl('', [EducatorValidators.subjectSpecialization]),
			experience_years: new FormControl(null, [EducatorValidators.experienceYears]),
			current_ctc: new FormControl('', [EducatorValidators.currentCtc]),
			expected_ctc: new FormControl('', [EducatorValidators.expectedCtc]),
			previous_institute: new FormControl('', [EducatorValidators.previousInstitute]),
			city_pref_1: new FormControl('', [EducatorValidators.cityPreference]),
			city_pref_2: new FormControl('', [EducatorValidators.cityPreference]),
			city_pref_3: new FormControl('', [EducatorValidators.cityPreference]),
			resume: new FormControl('')
		}, { validators: EducatorValidators.uniqueCities });
	}

	ngOnInit(): void {
		this.companyId = localStorage.getItem('company_id') || '';

		this.canViewFresher = this._authService.hasPermission(PermissionForStudents.CANDIDATE_VIEW_FRESHER);
		this.canViewExperienced = this._authService.hasPermission(PermissionForStudents.CANDIDATE_VIEW_EXPERIENCED);
		this.canViewEducator = this._authService.hasPermission(PermissionForStudents.CANDIDATE_VIEW_EDUCATOR);

		if (this.canViewFresher) {
			this.candidateTab = 'fresher';
		} else if (this.canViewExperienced) {
			this.candidateTab = 'experienced';
		} else if (this.canViewEducator) {
			this.candidateTab = 'educator';
		}
		this.searchSubject.pipe(debounceTime(500), distinctUntilChanged()).subscribe(() => {
			this.currentPage = 1;
			this.loadCandidates();
		});
		this.loadCandidates();
		this.loadStats();

		this.ExperiencedCandidateForm.get('current_ctc')?.valueChanges.subscribe(() => {
			this.ExperiencedCandidateForm.get('expected_ctc')?.updateValueAndValidity();
		});

		this.ExperiencedCandidateForm.get('city_pref_1')?.valueChanges.subscribe(() => {
			this.validatePreferredCities();
		});

		this.ExperiencedCandidateForm.get('city_pref_2')?.valueChanges.subscribe(() => {
			this.validatePreferredCities();
		});

		this.ExperiencedCandidateForm.get('city_pref_3')?.valueChanges.subscribe(() => {
			this.validatePreferredCities();
		});
		this.filteredCurrentLocations = this.indianLocations;
	}


	onSearchChange(): void {
		this.searchSubject.next(`${this.candidateSearchQuery}|${this.candidateFilters?.job_title}`);
	}


	get totalCandidatesCount(): number { return this.stats.total; }
	get onHoldCandidatesCount(): number { return this.stats.onHold; }
	get selectedCandidatesCount(): number { return this.stats.selected; }
	get rejectedCandidatesCount(): number { return this.stats.rejected; }


	switchTab(tab: 'fresher' | 'experienced' | 'educator'): void {
		this.candidateTab = tab;
		this.currentPage = 1;
		this.loadCandidates();
	}


	onFileSelected(event: any): void {
		const file = event.target.files[0];

		if (!file) {
			return;
		}

		const allowedExtensions = ['pdf', 'doc', 'docx'];
		const fileExtension = file.name.split('.').pop()?.toLowerCase();

		if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
			this.toastr.error('Only PDF, DOC and DOCX files are allowed.');
			event.target.value = '';
			return;
		}

		const maxSize = 5 * 1024 * 1024;

		if (file.size > maxSize) {
			this.toastr.error('Resume file size cannot exceed 5 MB.');
			event.target.value = '';
			return;
		}
		const fileNameRegex = /^[a-zA-Z0-9\s._()-]+$/;

		if (!fileNameRegex.test(file.name)) {
			this.toastr.error('File name contains invalid characters.');
			event.target.value = '';
			return;
		}

		this.selectedResumeFile = file;

		switch (this.popupExperienceType) {
			case 'fresher':
				this.FresherCandidateForm.patchValue({
					resume: file
				});
				this.FresherCandidateForm.get('resume')?.updateValueAndValidity();
				break;

			case 'experienced':
				this.ExperiencedCandidateForm.patchValue({
					resume: file
				});
				this.ExperiencedCandidateForm.get('resume')?.updateValueAndValidity();
				break;

			case 'educator':
				this.EducatorCandidateForm.patchValue({
					resume: file
				});
				this.EducatorCandidateForm.get('resume')?.updateValueAndValidity();
				break;
		}

		const sizeInMB = (file.size / (1024 * 1024)).toFixed(2);
		console.log(`Selected Resume: ${file.name} (${sizeInMB} MB)`);
	}


	clearFilters() {
		this.candidateFilters = {
			skill: '',
			subject: '',
			job_id: '',
			job_title: '',
			min_exp: null,
			max_exp: null,
			min_current_ctc: null,
			max_current_ctc: null,
			min_expected_ctc: null,
			max_expected_ctc: null,
			min_assessment_score: null,
			max_assessment_score: null,
			min_ai_score: null,
			max_ai_score: null,
		final_status: '',
			city: '',
			status: ''
		};
		this.selectedCandidateLocation = '';
		this.selectedCandidateExperience = '';
		this.selectedCandidateStatus = '';
		this.currentPage = 1;
		this.loadCandidates();
	}


	applyFilters(): void {

		if (this.candidateFilters.city) {
			this.candidateFilters.city = sanitizeCity(
				this.candidateFilters.city
			);
		}

		this.loadCandidates();
	}

	loadCandidates(): void {
		const searchQuery = this.candidateSearchQuery?.trim() || '';

		if (searchQuery !== this.lastSearchQuery) {
			this.currentPage = 1;
			this.lastSearchQuery = searchQuery;
		}
		this.isLoading = true;

		const filters = {
			candidate_type: this.candidateTab,
			status: this.selectedCandidateStatus || undefined,
			search: searchQuery || undefined,
			city: this.candidateFilters.city || undefined,
			skill: this.candidateFilters.skill || undefined,
			subject: this.candidateFilters.subject || undefined,
			job_id: this.candidateFilters.job_id || undefined,
			job_title: this.candidateFilters.job_title || undefined,
			min_exp: this.candidateFilters.min_exp ?? undefined,
			max_exp: this.candidateFilters.max_exp ?? undefined,
			min_current_ctc: this.candidateFilters.min_current_ctc ?? undefined,
			max_current_ctc: this.candidateFilters.max_current_ctc ?? undefined,
			min_expected_ctc: this.candidateFilters.min_expected_ctc ?? undefined,
			max_expected_ctc: this.candidateFilters.max_expected_ctc ?? undefined,
			min_assessment_score: this.candidateFilters.min_assessment_score ?? undefined,
			max_assessment_score: this.candidateFilters.max_assessment_score ?? undefined,
			min_ai_score: this.candidateFilters.min_ai_score ?? undefined,
			max_ai_score: this.candidateFilters.max_ai_score ?? undefined,
			final_status: this.candidateFilters.final_status || undefined,
			page: this.currentPage,
			limit: this.limit
		};
		this.candidateApi.listCandidates(filters).pipe(finalize(() => {
			this.isLoading = false;
		})
		).subscribe({
			next: (res) => {
				if (!res?.Success) {
					this.toastr.error(res?.Message || 'Failed to load candidates');
					return;
				}

				const dataObj = res?.Data || res?.data || {};
				const candidates = Array.isArray(dataObj?.candidates) ? dataObj.candidates : [];
				let allMapped: Candidate[] = candidates.map((item: any) => mapApiToCandidate(item));
				this.allCandidates = allMapped;
				this.filteredCandidates = allMapped;
				this.totalCandidates = dataObj?.total ?? 0
				this.totalPages = dataObj?.total_pages ?? 1
				this.hasPreviousPage = this.currentPage > 1;
				this.hasNextPage = this.currentPage < this.totalPages;
				this.updateEducatorValidators();
			},
			error: (error) => {
				this.toastr.error('Failed to load candidates');
			}
		});
	}


	selectExperience(experience: any): void {
		this.selectedCandidateExperience = experience.label;
		this.candidateFilters.min_exp = experience.minExp;
		this.candidateFilters.max_exp = experience.maxExp;
		this.isExperienceDropdownOpen = false;
		this.currentPage = 1;
		this.loadCandidates();
	}

	nextPage(): void {
		if (this.currentPage < this.totalPages && !this.isLoading) {
			this.currentPage++;
			this.loadCandidates();
		}
	}

	previousPage(): void {
		if (this.currentPage > 1 && !this.isLoading) {
			this.currentPage--;
			this.loadCandidates();
		}
	}

	loadStats(): void {
		this.candidateApi.getStats().subscribe({
			next: (res: any) => {

				if (res.Success) {
					const data = res.Data ?? res;
					const byStatus = data.by_status ?? {};
					this.stats = {
						total: data.total ?? 0,
						onHold: (byStatus['on hold'] ?? 0) + (byStatus['on_hold'] ?? 0),
						selected: (byStatus['selected'] ?? 0) + (byStatus['hired'] ?? 0),
						rejected: byStatus['rejected'] ?? 0,
					};
				} else {
					this.toastr.error(res?.Message || 'Failed to load stats');
				}
			},

			error: (err) => {
				this.toastr.error(err?.error?.detail || err?.message || 'Server error while loading stats');
			}
		});
	}


	allowDecimalNumbers(event: KeyboardEvent): void {
		const input = event.target as HTMLInputElement;
		const key = event.key;

		if (['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight'].includes(key)) {
			return;
		}
		if (/[0-9]/.test(key)) {
			return;
		}

		if (key === '.' && !input.value.includes('.')) {
			return;
		}

		event.preventDefault();
	}

	openAddPopup(): void {
		this.isAddPopupOpen = true;
		this.isEditMode = false;
		this.editingCandidateId = '';
		this.popupExperienceType = 'educator';
		this.popupTab = 'Manual';
		this.importResultMessage = '';
		this.resetForm();
		this.updateEducatorValidators();
	}

	editCandidate(candidate: Candidate): void {
		this.isAddPopupOpen = true;
		this.isEditMode = true;
		this.editingCandidateId = candidate.id;
		this.popupTab = 'Manual';
		this.importResultMessage = '';

		const type = candidate.experienceType ?? 'fresher';
		this.popupExperienceType = type;

		if (type === 'fresher') {

			this.FresherCandidateForm.patchValue({
				name: candidate.name,
				email: candidate.email,
				mobile: candidate.mobile,
				role: candidate.role,
				current_location: candidate.location === '-' ? '' : candidate.location,
				education: candidate.education,
				previous_institute: candidate.previousInstitute ?? '',
				year_of_passing: candidate.yearOfPassing ?? '',
				cgpa: candidate.cgpa,
				skills: Array.isArray(candidate.skills) ? candidate.skills.join(', ') : (candidate.skills ?? '')
			});

		} else if (type === 'experienced') {

			this.ExperiencedCandidateForm.patchValue({
				name: candidate.name ?? '',
				email: candidate.email ?? '',
				mobile: candidate.mobile ?? '',
				role: candidate.role ?? '',
				current_location: candidate.location === '-' ? '' : candidate.location,
				education: candidate.education ?? '',
				previous_institute: candidate.previousInstitute ?? '',
				year_of_passing: candidate.yearOfPassing ?? '',
				current_title: candidate.currentTitle ?? '',
				current_company: candidate.currentCompany ?? '',
				core_expertise: Array.isArray(candidate.core_expertise) ? candidate.core_expertise.join(', ') : (candidate.core_expertise ?? ''),
				experience_years: candidate.experience ? candidate.experience.replace(' yrs', '') : '',
				current_ctc: candidate.currentCtc ?? '',
				expected_ctc: candidate.expectedCtc ?? '',
				city_pref_1: candidate.prefCity1 ?? '',
				city_pref_2: candidate.prefCity2 ?? '',
				city_pref_3: candidate.prefCity3 ?? ''
			});

		} else {

			this.EducatorCandidateForm.patchValue({
				name: candidate.name ?? '',
				email: candidate.email ?? '',
				mobile: candidate.mobile ?? '',
				subject: candidate.subject ?? '',
				subject_specialization: candidate.specialization ?? '',
				experience_years: candidate.experience ? candidate.experience.replace(' yrs', '') : '',
				current_ctc: candidate.currentCtc ?? '',
				expected_ctc: candidate.expectedCtc ?? '',
				previous_institute: candidate.previousInstitute ?? '',
				city_pref_1: candidate.prefCity1 ?? '',
				city_pref_2: candidate.prefCity2 ?? '',
				city_pref_3: candidate.prefCity3 ?? ''
			});

		}
		this.updateEducatorValidators();
	}

	updateEducatorValidators(): void {
		const existingEmails = this.allCandidates
			.filter(c => !this.isEditMode || c.id !== this.editingCandidateId)
			.map(c => c.email)
			.filter(Boolean);

		const existingMobiles = this.allCandidates
			.filter(c => !this.isEditMode || c.id !== this.editingCandidateId)
			.map(c => c.mobile)
			.filter(Boolean);

		this.EducatorCandidateForm.get('email')?.setValidators([
			Validators.required,
			EducatorValidators.email,
			EducatorValidators.uniqueEmail(existingEmails)
		]);
		this.EducatorCandidateForm.get('mobile')?.setValidators([
			Validators.required,
			EducatorValidators.mobile,
			EducatorValidators.uniqueMobile(existingMobiles)
		]);

		this.EducatorCandidateForm.get('email')?.updateValueAndValidity();
		this.EducatorCandidateForm.get('mobile')?.updateValueAndValidity();
	}

	closeAddPopup(): void {
		this.isAddPopupOpen = false;
		this.isEditMode = false;
		this.editingCandidateId = '';
		this.importResultMessage = '';
		this.resetForm();
	}

	resetForm(): void {
		this.fullName = ''; this.email = ''; this.mobile = ''; this.role = '';
		this.currentLocation = ''; this.education = ''; this.cgpa = ''; this.skills = '';
		this.currentTitle = ''; this.currentCompany = ''; this.coreExpertise = '';
		this.totalExperience = ''; this.currentCtc = ''; this.expectedCtc = '';
		this.prefCity1 = ''; this.prefCity2 = ''; this.prefCity3 = '';
		this.eduSubject = ''; this.eduSpecialization = ''; this.eduExperience = '';
		this.eduCurrentCtc = ''; this.eduExpectedCtc = ''; this.eduPreviousInstitute = '';
		this.eduPrefCity1 = ''; this.eduPrefCity2 = ''; this.eduPrefCity3 = '';
		this.FresherCandidateForm?.reset();
		this.ExperiencedCandidateForm?.reset();
		this.EducatorCandidateForm?.reset();
		this.selectedResumeFile = null;
	}


	importCSV(): void {
		this.csvInputRef.nativeElement.click();
	}

	onCsvFileSelected(event: Event): void {
		const input = event.target as HTMLInputElement;
		const file = input.files?.[0];
		if (!file) return;

		const typeMap: Record<string, string> = { Fresher: 'fresher', Experienced: 'experienced', Educator: 'educator' };
		this.isSubmitting = true;

		this.candidateApi.bulkImportCsv(this.popupExperienceType, file).subscribe({
			next: (res) => {
				this.isSubmitting = false;
				const inserted = res.imported ?? res.inserted ?? res.created ?? 0;
				const skipped = res.skipped ?? 0;
				const errors = Array.isArray(res.errors) ? res.errors.length : (res.errors ?? 0);
				this.toastr.success('CSV imported successfully');
				this.importResultMessage = `Imported: ${inserted}, Skipped: ${skipped}, Errors: ${errors}`;

				this.candidateTab = this.popupExperienceType;
				this.closeAddPopup();

				input.value = '';
				this.loadCandidates();
				this.loadStats();
			},
			error: (err) => {
				this.isSubmitting = false;
				this.importResultMessage = err?.error?.detail ?? 'CSV import failed. Please check your file and try again.';
				input.value = '';
			},
		});
	}


	toggleCandidateJobDropdown(event: Event): void {
		event.stopPropagation();

		this.showCandidateJobDropdown = !this.showCandidateJobDropdown;

		if (this.showCandidateJobDropdown) {
			this.page = 1;
			this.jobSearchQuery = '';
			this.getAllJobs();
		}
	}


	downloadTemplate(): void {
		let headers = '';
		if (this.popupExperienceType === 'fresher') {
			headers = 'name,email,mobile,role,location,education,college_name,year_of_passing_out,cgpa,skills';
		} else if (this.popupExperienceType === 'experienced') {
			headers = 'name,email,mobile,role,location,education,college_name,year_of_passing_out,current_title,current_company,core_expertise,experience,current_ctc,expected_ctc,pref_city1,pref_city2,pref_city3';
		} else {
			headers = 'name,email,mobile,subject,goal,experience,currentCTC,expectedCTC,previousInstitute,prefCity1,prefCity2,prefCity3';
		}
		const blob = new Blob([headers], { type: 'text/csv' });
		const url = window.URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = `${this.popupExperienceType.toLowerCase()}_candidates_template.csv`;
		a.click();
		window.URL.revokeObjectURL(url);
	}


	deleteCandidate(candidateId: string): void {
		this.isCandidateDelete = candidateId;

		this.candidateApi.deleteCandidate(candidateId).pipe(
			tap((response) => {
				if (response.Success) {
					this.showDeleteConfirmPopup = false;
					this.selectedCandidateId = null;
					this.toastr.success('Candidate deleted successfully');
					this.loadCandidates();
					this.loadStats();
				} else {
					this.toastr.error(response.message);
				}
			}),
			finalize(() => {
				this.isCandidateDelete = null;
			})
		).subscribe();
	}


	openDeleteConfirm(candidateId: string): void {
		this.selectedCandidateId = candidateId;
		this.showDeleteConfirmPopup = true;
	}


	cancelDelete(): void {
		if (this.isCandidateDelete) {
			return;
		}
		this.showDeleteConfirmPopup = false;
		this.selectedCandidateId = null;
	}


	viewCandidate(id: string): void {
		this.router.navigate(['/recruiter-dashboard/candidates', id]);
	}

	toggleLocationDropdown(event: Event): void {
		event.stopPropagation();
		this.isFinalStatusDropdownOpen = false;
		this.isExperienceDropdownOpen = false;
		this.isLocationDropdownOpen = !this.isLocationDropdownOpen;
	}

	toggleExperienceDropdown(event: Event): void {
		event.stopPropagation();
		this.isFinalStatusDropdownOpen = false;
		this.isExperienceDropdownOpen = !this.isExperienceDropdownOpen;
	}

	
	toggleFinalStatusDropdown(event: Event): void {
		event.stopPropagation();
		this.isFinalStatusDropdownOpen = !this.isFinalStatusDropdownOpen;
		this.isExperienceDropdownOpen = false;
	}

	selectFinalStatus(status: string): void {
		this.candidateFilters.final_status = status;
		this.isFinalStatusDropdownOpen = false;
		this.currentPage = 1;
		this.applyFilters();
	}

	selectLocation(location: string): void {
		this.selectedCandidateLocation = location;
		this.candidateFilters.city = location;
		this.isLocationDropdownOpen = false;
		this.currentPage = 1;
		this.loadCandidates();
	}

	@HostListener('document:click')
	closeAllDropdowns(): void {
		this.isFinalStatusDropdownOpen = false;
		this.activeActionMenuId = null;
		this.isExperienceDropdownOpen = false;
		this.showJobDropdown = false;
		this.showCandidateJobDropdown = false;
	}

	toggleActionMenu(candidateId: string, event: Event): void {
		event.stopPropagation();
		this.activeActionMenuId = this.activeActionMenuId === candidateId ? null : candidateId;
	}

	closeActionMenu(): void {
		this.activeActionMenuId = null;
	}


	getScoreColorClass(score: number): string {
		if (score >= 85) return 'score-excellent';
		if (score >= 65) return 'score-good';
		return 'score-poor';
	}


	downloadResume(resumeUrl: string): void {
		if (!resumeUrl) {
			return;
		}

		const link = document.createElement('a');
		link.href = resumeUrl;
		link.target = '_blank';
		link.download = '';
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	}


	saveCandidate(): void {
		switch (this.popupExperienceType) {

			case 'fresher':
				this.submitFresherCandidate();
				break;

			case 'experienced':
				this.submitExperiencedCandidate();
				break;

			case 'educator':
				this.submitEducatorCandidate();
				break;
		}
	}


	submitFresherCandidate(): void {

		if (this.FresherCandidateForm.invalid) {
			this.FresherCandidateForm.markAllAsTouched();
			return;
		}

		const formValue = this.FresherCandidateForm.value;

		if (this.isEditMode) {

			const payload = {
				candidate_id: this.editingCandidateId,
				company_id: this.companyId,
				name: formValue.name,
				email: formValue.email,
				mobile: formValue.mobile,
				role: formValue.role,
				current_location: formValue.current_location,
				education: formValue.education,
				college_name: formValue.previous_institute,
				year_of_passing_out: formValue.year_of_passing === '' ? null : formValue.year_of_passing,
				cgpa: formValue.cgpa === '' ? null : formValue.cgpa,
				skills: formValue.skills
					? formValue.skills
						.split(',')
						.map((skill: string) => skill.trim())
						.filter((skill: string) => skill)
					: []
			};
			this.handleCandidateResponse(this.candidateApi.updateCandidate(this.editingCandidateId, payload));
		} else {

			const formData = new FormData();

			formData.append('company_id', this.companyId);
			formData.append('name', formValue.name);
			formData.append('email', formValue.email);
			formData.append('mobile', formValue.mobile);
			formData.append('role', formValue.role);
			formData.append('current_location', formValue.current_location);
			formData.append('education', formValue.education);

			if (formValue.previous_institute) {
				formData.append('college_name', formValue.previous_institute);
			}

			if (formValue.year_of_passing) {
				formData.append('year_of_passing_out', formValue.year_of_passing);
			}

			formData.append('candidate_type', 'fresher');

			if (formValue.cgpa) {
				formData.append('cgpa', formValue.cgpa);
			}

			if (formValue.skills) {
				formData.append('skills', formValue.skills);
			}

			if (this.selectedResumeFile) {
				formData.append('resume', this.selectedResumeFile);
			}
			this.handleCandidateResponse(this.candidateApi.postFresherAddCandidate(formData));
		}
	}


	submitExperiencedCandidate(): void {

		if (this.ExperiencedCandidateForm.invalid) {
			this.ExperiencedCandidateForm.markAllAsTouched();
			return;
		}

		const formValue = this.ExperiencedCandidateForm.value;

		if (this.isEditMode) {

			const payload = {
				candidate_id: this.editingCandidateId,
				company_id: this.companyId,
				name: formValue.name,
				email: formValue.email,
				mobile: formValue.mobile,
				role: formValue.role,
				current_location: formValue.current_location,
				education: formValue.education,
				college_name: formValue.previous_institute,
				year_of_passing_out: formValue.year_of_passing === '' ? null : formValue.year_of_passing,
				current_title: formValue.current_title,
				current_company: formValue.current_company,
				core_expertise: formValue.core_expertise
					? formValue.core_expertise
						.split(',')
						.map((exp: string) => exp.trim())
						.filter((exp: string) => exp)
					: [],
				experience_years: formValue.experience_years === '' ? null : formValue.experience_years,
				current_ctc: formValue.current_ctc === '' ? null : formValue.current_ctc,
				expected_ctc: formValue.expected_ctc === '' ? null : formValue.expected_ctc,
				city_pref_1: formValue.city_pref_1,
				city_pref_2: formValue.city_pref_2,
				city_pref_3: formValue.city_pref_3
			};
			this.handleCandidateResponse(this.candidateApi.updateCandidate(this.editingCandidateId, payload));
		} else {

			const formData = new FormData();

			formData.append('company_id', this.companyId);
			formData.append('name', formValue.name);
			formData.append('email', formValue.email);
			formData.append('mobile', formValue.mobile);
			formData.append('role', formValue.role);
			formData.append('current_location', formValue.current_location);
			formData.append('education', formValue.education);

			if (formValue.previous_institute) {
				formData.append('college_name', formValue.previous_institute);
			}

			if (formValue.year_of_passing) {
				formData.append('year_of_passing_out', formValue.year_of_passing);
			}

			formData.append('current_title', formValue.current_title);
			formData.append('current_company', formValue.current_company);

			if (formValue.core_expertise) {
				formData.append('core_expertise', formValue.core_expertise);
			}

			if (formValue.experience_years !== null && formValue.experience_years !== undefined && formValue.experience_years !== '') {
				formData.append('experience_years', formValue.experience_years);
			}
			if (formValue.current_ctc !== null && formValue.current_ctc !== undefined && formValue.current_ctc !== '') {
				formData.append('current_ctc', String(formValue.current_ctc));
			}
			if (formValue.expected_ctc !== null && formValue.expected_ctc !== undefined && formValue.expected_ctc !== '') {
				formData.append('expected_ctc', String(formValue.expected_ctc));
			}
			formData.append('candidate_type', 'experienced');

			if (formValue.city_pref_1) {
				formData.append('city_pref_1', formValue.city_pref_1);
			}

			if (formValue.city_pref_2) {
				formData.append('city_pref_2', formValue.city_pref_2);
			}

			if (formValue.city_pref_3) {
				formData.append('city_pref_3', formValue.city_pref_3);
			}

			if (this.selectedResumeFile) {
				formData.append('resume', this.selectedResumeFile);
			}

			this.handleCandidateResponse(this.candidateApi.postExperiencedAddCandidate(formData));
		}
	}


	submitEducatorCandidate(): void {

		if (this.EducatorCandidateForm.invalid) {
			this.EducatorCandidateForm.markAllAsTouched();
			return;
		}

		const formValue = this.EducatorCandidateForm.value;

		if (this.isEditMode) {

			const payload = {
				candidate_id: this.editingCandidateId,
				company_id: this.companyId,
				name: formValue.name,
				email: formValue.email,
				mobile: formValue.mobile,
				subject: formValue.subject,
				subject_specialization: formValue.subject_specialization,
				experience_years: formValue.experience_years === '' ? null : formValue.experience_years,
				current_ctc: formValue.current_ctc === '' ? null : formValue.current_ctc,
				expected_ctc: formValue.expected_ctc === '' ? null : formValue.expected_ctc,
				previous_institute: formValue.previous_institute,
				city_pref_1: formValue.city_pref_1,
				city_pref_2: formValue.city_pref_2,
				city_pref_3: formValue.city_pref_3
			};

			this.handleCandidateResponse(
				this.candidateApi.updateCandidate(this.editingCandidateId, payload)
			);
		}
		else {

			const formData = new FormData();
			formData.append('company_id', this.companyId);
			formData.append('name', formValue.name);
			formData.append('email', formValue.email);
			formData.append('mobile', formValue.mobile);
			formData.append('subject', formValue.subject);
			formData.append('subject_specialization', formValue.subject_specialization);

			if (formValue.experience_years !== null && formValue.experience_years !== undefined && formValue.experience_years !== '') {
				formData.append('experience_years', formValue.experience_years);
			}
			if (formValue.expected_ctc !== null && formValue.expected_ctc !== undefined && formValue.expected_ctc !== '') {
				formData.append('expected_ctc', formValue.expected_ctc);
			}
			if (formValue.previous_institute) {
				formData.append('previous_institute', formValue.previous_institute);
			}
			formData.append('candidate_type', 'educator');

			if (formValue.current_ctc !== null && formValue.current_ctc !== undefined && formValue.current_ctc !== '') {
				formData.append('current_ctc', formValue.current_ctc);
			}

			if (formValue.city_pref_1) {
				formData.append('city_pref_1', formValue.city_pref_1);
			}

			if (formValue.city_pref_2) {
				formData.append('city_pref_2', formValue.city_pref_2);
			}

			if (formValue.city_pref_3) {
				formData.append('city_pref_3', formValue.city_pref_3);
			}

			if (this.selectedResumeFile) {
				formData.append('resume', this.selectedResumeFile);
			}
			this.handleCandidateResponse(
				this.candidateApi.postEducatorAddCandidate(formData)
			);
		}
	}


	handleCandidateResponse(request$: any): void {

		this.isSubmitting = true;

		request$.subscribe({
			next: (response: any) => {

				this.isSubmitting = false;

				if (response?.Success) {

					this.toastr.success(
						this.isEditMode
							? 'Candidate updated successfully'
							: 'Candidate added successfully'
					);

					this.FresherCandidateForm.reset();
					this.ExperiencedCandidateForm.reset();
					this.EducatorCandidateForm.reset();
					this.selectedResumeFile = null;
					this.candidateTab = this.popupExperienceType;
					this.closeAddPopup();
					this.loadCandidates();
					this.loadStats();

				} else {
					this.toastr.error(response?.Message || 'Something went wrong');
				}
			},
			error: (err: any) => {

				this.isSubmitting = false;

				this.toastr.error(
					err?.error?.detail ||
					err?.error?.message ||
					'Operation failed'
				);

			}
		});

	}


	downloadUserData() {
		this.candidateApi.exportCandidateTemplate(this.companyId, this.candidateTab).subscribe({
			next: (blob: Blob) => {
				const url = window.URL.createObjectURL(blob);
				const a = document.createElement('a');
				a.href = url;
				a.download = 'candidates.xlsx';
				document.body.appendChild(a);
				a.click();
				document.body.removeChild(a);
				window.URL.revokeObjectURL(url);
				this.toastr.success('Template downloaded successfully');
			},
			error: () => {
				this.toastr.error('Failed to download template');
			}
		});
	}


	onMobilePaste(event: ClipboardEvent): void {
		const pastedText = event.clipboardData?.getData('text') || '';

		if (!/^\d+$/.test(pastedText)) {
			event.preventDefault();
		}
	}


	getAllJobs(): void {
		this.isJobLoading = true;
		let status = 'active';
		const filters = {
			job_type: this.selectedJobType,
			work_mode: this.selectedJobLocation,
			is_visible: this.selectedVisibility,
		};

		this.jobService.getAllJobPosting(
			this.companyId,
			this.page,
			this.jobLimit,
			status,
			this.jobSearchQuery,
			filters
		).pipe(tap(response => {
			if (response?.Success) {
				this.allPostJobs = response.Data.jobs;
				this.total = response.Data.total;
				this.totalPage = response.Data.total_pages;
				this.jobLimit = response.Data.limit;
				this.page = response.Data.page;
			} else {
				this.toastr.error(response?.Message);
			}
		}),
			finalize(() => {
				this.isJobLoading = false;
			}),
			catchError(() => {
				return of(null);
			})
		).subscribe();
	}


	selectJob(job: any): void {
		this.ExperiencedCandidateForm.get('role')?.setValue(job.job_title);
		this.jobSearchQuery = job.job_title;
		this.showJobDropdown = false;
	}


	closeJobDropdown(): void {
		setTimeout(() => {
			this.showJobDropdown = false;
		}, 200);
	}


	onJobDropdownScroll(event: any): void {
		const element = event.target;

		if (
			element.scrollTop + element.clientHeight >= element.scrollHeight - 10 &&
			this.page < this.totalPage &&
			!this.isLoading
		) {
			this.page++;
			this.getAllJobs();
		}
	}


	previousJobPage(event: MouseEvent): void {
		event.preventDefault();
		event.stopPropagation();

		if (this.page > 1) {
			this.page--;
			this.getAllJobs();
		}
	}

	nextJobPage(event: MouseEvent): void {
		event.preventDefault();
		event.stopPropagation();

		if (this.page < this.totalPage) {
			this.page++;
			this.getAllJobs();
		}
	}


	onJobSearch(): void {
		this.jobSearchQuery = this.ExperiencedCandidateForm.get('role')?.value || '';
		this.page = 1;
		this.getAllJobs();
	}


	filterCurrentLocation(): void {
		const value = this.FresherCandidateForm.get('current_location')?.value?.toLowerCase() || '';
		this.filteredCurrentLocations = this.indianLocations.filter((city: string) => city.toLowerCase().includes(value));
		this.showCurrentLocationDropdown = true;
	}

	onCurrentLocationFocus(): void {
		this.filteredCurrentLocations = [...this.indianLocations];
		this.showCurrentLocationDropdown = true;
	}

	selectCurrentLocation(city: string): void {
		this.FresherCandidateForm.get('current_location')?.setValue(city);
		this.showCurrentLocationDropdown = false;
	}

	closeCurrentLocationDropdown(): void {
		setTimeout(() => {
			this.showCurrentLocationDropdown = false;
		}, 200);
	}

	onFresherJobSearch(): void {
		this.jobSearchQuery = this.FresherCandidateForm.get('role')?.value || '';
		this.page = 1;
		this.getAllJobs();
	}


	selectFresherJob(job: CompanyJob): void {
		this.FresherCandidateForm.patchValue({ role: job.job_title });
		this.showJobDropdown = false;
	}


	selectCandidateJob(job: CompanyJob): void {
		this.candidateFilters.job_title = job.job_title;
		this.showCandidateJobDropdown = false;
		this.currentPage = 1;
		this.loadCandidates();
	}


	toggleJobDropdown(event: Event): void {
		event.stopPropagation();
		this.showJobDropdown = !this.showJobDropdown;
		if (this.showJobDropdown) {
			this.page = 1;
			this.jobSearchQuery = '';
			this.getAllJobs();
		}
	}


	onCandidateJobSearch(): void {
		this.page = 1;
		this.getAllJobs();
	}


	onFresherJobFocus(): void {
		this.showJobDropdown = true;
		this.page = 1;
		this.jobSearchQuery = '';
		this.getAllJobs();
	}


	get startIndex(): number {
		if (this.totalCandidates === 0 || this.filteredCandidates.length === 0) return 0;
		return (this.currentPage - 1) * this.limit + 1;
	}


	get endIndex(): number {
		if (this.totalCandidates === 0 || this.filteredCandidates.length === 0) return 0;
		const calculatedEnd = (this.currentPage - 1) * this.limit + this.filteredCandidates.length;
		return Math.min(calculatedEnd, this.totalCandidates);
	}


	clearCandidateJob(event: Event): void {
		event.stopPropagation();

		this.candidateFilters.job_title = '';
		this.currentPage = 1;
		this.loadCandidates();
	}


	candidateFilters = {
		skill: '',
		subject: '',
		job_id: '',
		job_title: '',
		min_exp: null,
		max_exp: null,
		min_current_ctc: null,
		max_current_ctc: null,
		min_expected_ctc: null,
		max_expected_ctc: null,
		min_assessment_score: null,
		max_assessment_score: null,
		min_ai_score: null,
		max_ai_score: null,
		final_status: '',
		city: '',
		status: ''
	};


	hasValidationErrors(): boolean {
		return !!(
			validateCity(this.candidateFilters.city) ||
			validateRange(this.candidateFilters.min_exp, this.candidateFilters.max_exp, 50, 'experience') ||
			validateRange(this.candidateFilters.min_assessment_score, this.candidateFilters.max_assessment_score, 100, 'assessment score') ||
			validateRange(this.candidateFilters.min_ai_score, this.candidateFilters.max_ai_score, 100, 'AI interview score') ||
			validateRange(this.candidateFilters.min_current_ctc, this.candidateFilters.max_current_ctc, 100, 'current CTC') ||
			validateRange(this.candidateFilters.min_expected_ctc, this.candidateFilters.max_expected_ctc, 100, 'expected CTC')
		);
	}

	onCityChange(value: string): void {
		this.candidateFilters.city = sanitizeCity(value);
	}


	onlyAllowDigits(event: KeyboardEvent): void {
		const charCode = event.which ? event.which : event.keyCode;
		if (charCode > 31 && (charCode < 48 || charCode > 57)) {
			event.preventDefault();
		}
	}


	restrictCtcInput(event: Event, controlName: string): void {
		const input = event.target as HTMLInputElement;
		let value = input.value;

		value = value.replace(/[^0-9.]/g, '');
		const dotIndex = value.indexOf('.');
		if (dotIndex !== -1) {
			value = value.slice(0, dotIndex + 1) + value.slice(dotIndex + 1).replace(/\./g, '');
			const parts = value.split('.');
			if (parts[1] && parts[1].length > 2) {
				value = parts[0] + '.' + parts[1].slice(0, 2);
			}
		}
		const num = parseFloat(value);
		if (!isNaN(num) && num > 1000) {
			value = '1000';
		}
		this.EducatorCandidateForm.get(controlName)?.setValue(value, { emitEvent: true });
		input.value = value;
	}


	restrictExperienceInput(event: Event): void {
		const input = event.target as HTMLInputElement;
		let value = input.value;

		value = value.replace(/[^0-9.]/g, '');
		const dotIndex = value.indexOf('.');
		if (dotIndex !== -1) {
			value = value.slice(0, dotIndex + 1) + value.slice(dotIndex + 1).replace(/\./g, '');
			const parts = value.split('.');
			if (parts[1] && parts[1].length > 2) {
				value = parts[0] + '.' + parts[1].slice(0, 2);
			}
		}
		const num = parseFloat(value);
		if (!isNaN(num) && num > 50) {
			value = '50';
		}
		this.EducatorCandidateForm.get('experience_years')?.setValue(value, { emitEvent: true });
		input.value = value;
	}

	blockNumberKeys(event: KeyboardEvent): void {
		if (/[0-9]/.test(event.key)) {
			event.preventDefault();
		}
	}

	filterCities(controlName: 'city_pref_1' | 'city_pref_2' | 'city_pref_3'): void {
		const query = (this.EducatorCandidateForm.get(controlName)?.value || '').trim().toLowerCase();
		const filtered = this.indianLocations.filter(city => city.toLowerCase().includes(query));

		if (controlName === 'city_pref_1') {
			this.filteredCities1 = filtered;
			this.showCityDropdown1 = filtered.length > 0;
		} else if (controlName === 'city_pref_2') {
			this.filteredCities2 = filtered;
			this.showCityDropdown2 = filtered.length > 0;
		} else if (controlName === 'city_pref_3') {
			this.filteredCities3 = filtered;
			this.showCityDropdown3 = filtered.length > 0;
		}
	}

	onCityFocus(controlName: 'city_pref_1' | 'city_pref_2' | 'city_pref_3'): void {
		this.filterCities(controlName);
	}

	selectCity(controlName: 'city_pref_1' | 'city_pref_2' | 'city_pref_3', city: string): void {
		this.EducatorCandidateForm.get(controlName)?.setValue(city);
		this.EducatorCandidateForm.get(controlName)?.markAsTouched();

		if (controlName === 'city_pref_1') {
			this.showCityDropdown1 = false;
		} else if (controlName === 'city_pref_2') {
			this.showCityDropdown2 = false;
		} else if (controlName === 'city_pref_3') {
			this.showCityDropdown3 = false;
		}
	}

	closeCityDropdown(controlName: 'city_pref_1' | 'city_pref_2' | 'city_pref_3'): void {
		setTimeout(() => {
			if (controlName === 'city_pref_1') {
				this.showCityDropdown1 = false;
			} else if (controlName === 'city_pref_2') {
				this.showCityDropdown2 = false;
			} else if (controlName === 'city_pref_3') {
				this.showCityDropdown3 = false;
			}
		}, 200);
	}

	validatePreferredCities(): void {
		const city1Control = this.ExperiencedCandidateForm.get('city_pref_1');
		const city2Control = this.ExperiencedCandidateForm.get('city_pref_2');
		const city3Control = this.ExperiencedCandidateForm.get('city_pref_3');

		const city1 = city1Control?.value?.trim().toLowerCase();
		const city2 = city2Control?.value?.trim().toLowerCase();
		const city3 = city3Control?.value?.trim().toLowerCase();

		// Clear previous duplicate errors
		if (city2Control?.hasError('duplicateCity')) {
			city2Control.setErrors(null);
		}

		if (city3Control?.hasError('duplicateCity')) {
			city3Control.setErrors(null);
		}

		// Validate City 2
		if (city1 && city2 && city1 === city2) {
			city2Control?.setErrors({ duplicateCity: true });
		}

		// Validate City 3
		if (city3 && (city3 === city1 || city3 === city2)) {
			city3Control?.setErrors({ duplicateCity: true });
		}
	}

	filterExperiencedCities(
		controlName: 'city_pref_1' | 'city_pref_2' | 'city_pref_3'
	): void {

		const query = (
			this.ExperiencedCandidateForm.get(controlName)?.value || ''
		).trim().toLowerCase();

		const filtered = this.indianLocations.filter(city =>
			city.toLowerCase().includes(query)
		);


		if (controlName === 'city_pref_1') {
			this.filteredExperiencedCities1 = filtered;
			this.showExperiencedCityDropdown1 = filtered.length > 0;

		} else if (controlName === 'city_pref_2') {
			this.filteredExperiencedCities2 = filtered;
			this.showExperiencedCityDropdown2 = filtered.length > 0;

		} else if (controlName === 'city_pref_3') {
			this.filteredExperiencedCities3 = filtered;
			this.showExperiencedCityDropdown3 = filtered.length > 0;
		}
	}

	onExperiencedCityFocus(
		controlName: 'city_pref_1' | 'city_pref_2' | 'city_pref_3'
	) {
		this.filterExperiencedCities(controlName);
	}

	selectExperiencedCity(
		controlName: 'city_pref_1' | 'city_pref_2' | 'city_pref_3',
		city: string
	) {

		this.ExperiencedCandidateForm
			.get(controlName)
			?.setValue(city);

		this.ExperiencedCandidateForm
			.get(controlName)
			?.markAsTouched();


		if (controlName === 'city_pref_1') {
			this.showExperiencedCityDropdown1 = false;
		}
		else if (controlName === 'city_pref_2') {
			this.showExperiencedCityDropdown2 = false;
		}
		else {
			this.showExperiencedCityDropdown3 = false;
		}
	}

	closeExperiencedCityDropdown(
		controlName: 'city_pref_1' | 'city_pref_2' | 'city_pref_3'
	) {

		setTimeout(() => {

			if (controlName === 'city_pref_1') {
				this.showExperiencedCityDropdown1 = false;
			}
			else if (controlName === 'city_pref_2') {
				this.showExperiencedCityDropdown2 = false;
			}
			else {
				this.showExperiencedCityDropdown3 = false;
			}

		}, 200);

	}


}
